import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Press } from '@dorixona/ui'
import { Icon } from '../../ui/Icon'
import { nativeErrorCode } from '../../app/nativeBridge'
import { type SecurityOperation, type SecurityStatus, useNativeSecurity } from './security'

/** Отказы, о которых есть что сказать; остальное — «не получилось». */
const KNOWN_ERRORS = [
  'BUSY',
  'LOCKED',
  'UNAUTHORIZED',
  'STORAGE_ERROR',
  'BIOMETRIC_UNAVAILABLE',
  'PIN_REQUIRED',
]

/**
 * PIN-код и вход по биометрии — только в нашем Flutter-приложении и только у
 * вошедшего. Строки открывают окна оболочки; результат — то, что она
 * ответила. Оболочка старая и не ответила — раздела нет.
 */
export function SecuritySettings({ customerId }: { customerId: number }) {
  const { t } = useTranslation('account')
  const { status, run } = useNativeSecurity(customerId)
  const [message, setMessage] = useState('')

  if (!status) return null

  // Пока оболочка отвечает, строки и переключатель выключены — второго окна не будет.
  const perform = (operation: SecurityOperation) => {
    setMessage('')
    run.mutate(operation, {
      onError: (error) => {
        const code = nativeErrorCode(error)
        // Покупатель сам закрыл окно оболочки — ничего не случилось.
        if (code === 'CANCELLED') return
        setMessage(
          KNOWN_ERRORS.includes(code) ? t(`security.errors.${code}`) : t('security.errors.failed'),
        )
      },
    })
  }

  const row = (title: string, operation: SecurityOperation, value = '', danger = false) => (
    <Press
      squish="row"
      className={danger ? 'acc-pick acc-pick--danger' : 'acc-pick'}
      disabled={run.isPending}
      onPress={() => {
        perform(operation)
      }}
    >
      <span className="t1">{title}</span>
      {value ? <span className="acc-pick__val">{value}</span> : null}
      <Icon name="i-chev" size={16} />
    </Press>
  )

  return (
    <div className="acc-sec acc-security">
      <div className="acc-sh">
        <h3>{t('security.title')}</h3>
      </div>
      {status.hasPin ? (
        <>
          {row(t('security.changePin'), { action: 'security.changePin' })}
          {row(t('security.removePin'), { action: 'security.removePin' }, '', true)}
          {status.biometricSupported ? (
            <BiometricSwitch
              status={status}
              pending={run.isPending}
              onChange={(enabled) => {
                perform({ action: 'security.setBiometricEnabled', enabled })
              }}
            />
          ) : null}
        </>
      ) : (
        row(t('security.setPin'), { action: 'security.setupPin' }, t('security.pinOff'))
      )}
      {message ? (
        <p className="acc-note acc-note-err" role="status">
          {message}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Один переключатель на любой способ: лицо, отпечаток или оба выбирает
 * телефон, подпись лишь подсказывает, что он умеет. Положение — всегда то,
 * что подтвердила оболочка.
 */
function BiometricSwitch({
  status,
  pending,
  onChange,
}: {
  status: SecurityStatus
  pending: boolean
  onChange: (enabled: boolean) => void
}) {
  const { t } = useTranslation('account')
  const mode = status.displayMode === 'unavailable' ? 'generic' : status.displayMode
  const title = t(`security.biometric.${mode}`)
  const hint = status.biometricAvailable
    ? t('security.biometric.on')
    : status.biometricEnrolled
      ? t('security.biometric.unavailable')
      : t('security.biometric.setup')

  return (
    <label className="acc-toggle">
      <div>
        <div className="t1">{title}</div>
        <div className="t2">{hint}</div>
      </div>
      <span className="switch">
        <input
          type="checkbox"
          role="switch"
          aria-label={title}
          checked={status.biometricEnabled}
          disabled={pending || (!status.biometricEnabled && !status.canEnableBiometrics)}
          onChange={(event) => {
            onChange(event.target.checked)
          }}
        />
        <span className="tr2" />
        <span className="th" />
      </span>
    </label>
  )
}
